import { RateInterval } from '__types__/enums';
import type { FeePercents } from 'v5/core/booking-billing/model';
import type { RateFormValue } from 'v5/core/entity-values/values.types';

/**
 * Billing units a resource (contact) rate may use. `ContactRate.jsonSchema`
 * accepts DAILY, HOURLY and FIXED only; ITEM belongs to expenses and the
 * backend rejects it on `contact_rates`.
 */
export const RESOURCE_RATE_INTERVALS = [
  RateInterval.Daily,
  RateInterval.Hourly,
  RateInterval.Fixed
] as const;

export type ResourceRateInterval = (typeof RESOURCE_RATE_INTERVALS)[number];

export function isResourceRateInterval(
  interval: RateInterval
): interval is ResourceRateInterval {
  return (RESOURCE_RATE_INTERVALS as ReadonlyArray<RateInterval>).includes(
    interval
  );
}

/**
 * Fee columns of a rate in the order bookings apply them
 * (`bookingFees.apply`): agency on the rate, payroll on the rate minus
 * agency, processing on the subtotal.
 */
export const RATE_FEES = [
  { column: 'agency_fee', key: 'agency' },
  { column: 'payroll_fee', key: 'payroll' },
  { column: 'processing_fee', key: 'processing' }
] as const satisfies ReadonlyArray<{
  column: keyof RateFormValue;
  key: keyof FeePercents;
}>;

export type RateFee = (typeof RATE_FEES)[number];
export type RateFeeColumn = RateFee['column'];

/** Fee percentages of a rate as bookings read them (no fee = 0). */
export function feePercentsOf(
  rate: Pick<RateFormValue, RateFeeColumn>
): FeePercents {
  return {
    agency: rate.agency_fee ?? 0,
    payroll: rate.payroll_fee ?? 0,
    processing: rate.processing_fee ?? 0
  };
}
